import { useState, useEffect, useCallback, useRef } from 'react';
import cloudflareAPI from '../../services/api/cloudflareAPI';
import type { AISignal, MarketSummary } from '../../services/api/cloudflareAPI';
import { getConfidenceLevel as getConfidenceLevelUtil } from '../../utils/dateUtils';

// Check if IDX market is open
// Sesi 1: 09:00 - 12:00 WIB, Sesi 2: 13:30 - 16:00 WIB (Mon-Fri)
export function isMarketOpen(): boolean {
  const now = new Date();
  const wibOffset = 7 * 60; // WIB is UTC+7
  const utcMinutes = now.getUTCHours() * 60 + now.getUTCMinutes();
  const wibMinutes = (utcMinutes + wibOffset) % (24 * 60);
  const dayOfWeek = now.getUTCDay();

  // Market closed on weekends
  if (dayOfWeek === 0 || dayOfWeek === 6) return false;

  // Sesi 1: 09:00 - 12:00 WIB (540 - 720 minutes)
  // Sesi 2: 13:30 - 16:00 WIB (810 - 960 minutes)
  return (wibMinutes >= 540 && wibMinutes < 720) ||
         (wibMinutes >= 810 && wibMinutes < 960);
}

// Get current IDX session label
export function getMarketSession(): { label: string; nextOpen: string } {
  const now = new Date();
  const wibOffset = 7 * 60;
  const utcMinutes = now.getUTCHours() * 60 + now.getUTCMinutes();
  const wibMinutes = (utcMinutes + wibOffset) % (24 * 60);
  const dayOfWeek = now.getUTCDay();

  if (dayOfWeek === 0 || dayOfWeek === 6) {
    return { label: 'Akhir Pekan', nextOpen: 'Senin 09:00 WIB' };
  }
  if (wibMinutes < 540) {
    return { label: 'Pasar Tutup', nextOpen: '09:00 WIB' };
  }
  if (wibMinutes >= 540 && wibMinutes < 720) {
    return { label: 'Sesi 1', nextOpen: '' };
  }
  if (wibMinutes >= 720 && wibMinutes < 810) {
    return { label: 'Istirahat Siang', nextOpen: '13:30 WIB' };
  }
  if (wibMinutes >= 810 && wibMinutes < 960) {
    return { label: 'Sesi 2', nextOpen: '' };
  }
  // Jumat sore → buka Senin, bukan "Besok"
  if (dayOfWeek === 5) {
    return { label: 'Pasar Tutup', nextOpen: 'Senin 09:00 WIB' };
  }
  return { label: 'Pasar Tutup', nextOpen: 'Besok 09:00 WIB' };
}

// Re-export types from cloudflareAPI and enhanced types
export type { AISignal, AISignalsResponse, MarketSummary } from '../../services/api/cloudflareAPI';

interface UseAISignalsResult {
  signals: AISignal[];
  loading: boolean;
  error: string | null;
  marketOutlook: string | null;
  riskWarning: string | null;
  averageConfidence: number;
  refreshSignals: () => Promise<void>;
  lastUpdate: Date | null;
  isMarketOpen: boolean;
  marketSession: { label: string; nextOpen: string };
}

// ─── localStorage cache helpers ─────────────────────────────────────────────
const CACHE_KEY_PREFIX = 'tt_signals_';

// Key includes tier info from last fetch to avoid stale data after tier change.
// The tier is derived from signal count: FREE=5, PRO=99.
function getCacheKey(): string {
  return CACHE_KEY_PREFIX + 'v3';
}

interface CachedSignals {
  signals: AISignal[];
  marketOutlook: string | null;
  riskWarning: string | null;
  averageConfidence: number;
  cachedAt: number; // Date.now()
  signalCount: number; // used to detect tier change
}

function readCache(): CachedSignals | null {
  try {
    const raw = localStorage.getItem(getCacheKey());
    if (!raw) return null;
    const parsed: CachedSignals = JSON.parse(raw);
    if (!parsed.signals || !Array.isArray(parsed.signals)) return null;
    return parsed;
  } catch { return null; }
}

function writeCache(data: CachedSignals): void {
  try {
    localStorage.setItem(getCacheKey(), JSON.stringify(data));
  } catch { /* quota exceeded — ignore */ }
}

// ─── Hook ───────────────────────────────────────────────────────────────────

export function useAISignals(): UseAISignalsResult {
  const [signals, setSignals] = useState<AISignal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [marketOutlook, setMarketOutlook] = useState<string | null>(null);
  const [riskWarning, setRiskWarning] = useState<string | null>(null);
  const [averageConfidence, setAverageConfidence] = useState(0);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const marketOpen = isMarketOpen();

  const fetchInProgressRef = useRef(false);

  const applyData = useCallback((data: CachedSignals) => {
    setSignals(data.signals);
    setMarketOutlook(data.marketOutlook);
    setRiskWarning(data.riskWarning);
    setAverageConfidence(data.averageConfidence);
    setLastUpdate(new Date(data.cachedAt));
  }, []);

  const fetchFromAPI = useCallback(async () => {
    if (fetchInProgressRef.current) return;
    fetchInProgressRef.current = true;
    try {
      const signalsData = await cloudflareAPI.getAISignals();
      const sigArr = signalsData.signals || [];
      const cacheEntry: CachedSignals = {
        signals: sigArr,
        marketOutlook: signalsData.marketOutlook,
        riskWarning: signalsData.riskWarning,
        averageConfidence: signalsData.averageConfidence || 0,
        cachedAt: Date.now(),
        signalCount: sigArr.length,
      };
      writeCache(cacheEntry);
      applyData(cacheEntry);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch AI signals:', err);
      setError(err instanceof Error ? err.message : 'Gagal memuat sinyal');
    } finally {
      setLoading(false);
      fetchInProgressRef.current = false;
    }
  }, [applyData]);

  // Mount: load from cache first, then fetch in background
  useEffect(() => {
    const cache = readCache();
    if (cache && cache.signals.length > 0) {
      // Instantly show cached data
      applyData(cache);
      setLoading(false);
    }
    // Always refresh in background — cache is for instant paint only.
    // Without this, stale cache (e.g. backend deployed a fix) lingers up to 15min.
    void fetchFromAPI();
  }, [applyData, fetchFromAPI]);

  const refreshSignals = useCallback(async () => {
    setLoading(true);
    await fetchFromAPI();
  }, [fetchFromAPI]);

  return {
    signals,
    loading,
    error,
    marketOutlook,
    riskWarning,
    averageConfidence,
    refreshSignals,
    lastUpdate,
    isMarketOpen: marketOpen,
    marketSession: getMarketSession()
  };
}

// Market summary hook — no longer used by MorningBriefingWidget (uses useMarketSummary from separate file)
// Kept for backward compatibility with other pages
export function useMarketSummary() {
  const [summary, setSummary] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [marketConditions, setMarketConditions] = useState<MarketSummary['marketConditions'] | null>(null);

  const fetchMarketSummary = useCallback(async () => {
    try {
      setError(null);
      setLoading(true);

      const marketSummary = await cloudflareAPI.getMarketSummary();

      setSummary(marketSummary.summary);
      setMarketConditions(marketSummary.marketConditions);
      setLastUpdate(new Date());

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch market summary';
      setError(errorMessage);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchMarketSummary();
  }, [fetchMarketSummary]);

  return {
    summary,
    loading,
    error,
    lastUpdate,
    marketConditions,
    refreshSummary: fetchMarketSummary
  };
}

// Utility functions for AI signals
export function getSignalColor(direction: string): string {
  switch(direction) {
    case 'NAIK': return 'text-green-600';
    case 'TURUN': return 'text-red-600';
    case 'NETRAL': return 'text-yellow-600';
    default: return 'text-gray-600';
  }
}

export function getSignalBgColor(direction: string): string {
  switch(direction) {
    case 'NAIK': return 'bg-green-50';
    case 'TURUN': return 'bg-red-50';
    case 'NETRAL': return 'bg-yellow-50';
    default: return 'bg-gray-50';
  }
}

export function getConfidenceColor(confidence: number): string {
  if (confidence >= 75) return 'text-emerald-600';
  if (confidence >= 60) return 'text-yellow-600';
  return 'text-orange-600';
}

export function getConfidenceBgColor(confidence: number): string {
  if (confidence >= 75) return 'bg-emerald-50 border-emerald-200';
  if (confidence >= 60) return 'bg-yellow-50 border-yellow-200';
  return 'bg-orange-50 border-orange-200';
}

export function getRiskLabel(riskLevel: string): { label: string; color: string } {
  switch(riskLevel) {
    case 'LOW': return { label: 'Rendah', color: 'text-emerald-600' };
    case 'MEDIUM': return { label: 'Sedang', color: 'text-yellow-600' };
    case 'HIGH': return { label: 'Tinggi', color: 'text-orange-600' };
    case 'CRITICAL': return { label: 'Sangat Tinggi', color: 'text-red-600' };
    default: return { label: 'Sedang', color: 'text-yellow-600' };
  }
}

export function getPriorityColor(priority: 'LOW' | 'MEDIUM' | 'HIGH'): string {
  switch (priority) {
    case 'HIGH': return 'bg-red-100 text-red-800';
    case 'MEDIUM': return 'bg-yellow-100 text-yellow-800';
    case 'LOW': return 'bg-gray-100 text-gray-800';
    default: return 'bg-gray-100 text-gray-800';
  }
}

// Re-export centralized confidence utility
export function getConfidenceLevel(confidence: number): { level: string; color: string } {
  const result = getConfidenceLevelUtil(confidence);
  return { level: result.label, color: result.color };
}

export function formatPrice(price: number, _instrument: string): string {
  // All IDX stocks use IDR with no decimal places
  return Math.round(price).toLocaleString('id-ID');
}

export function calculatePercentChange(entry: number, target: number): number {
  return ((target - entry) / entry) * 100;
}

export function isSignalExpired(expiresAt: string): boolean {
  return new Date(expiresAt) <= new Date();
}

export function getTimeToExpiry(expiresAt: string): string {
  const now = new Date();
  const expiry = new Date(expiresAt);
  const diff = expiry.getTime() - now.getTime();

  if (diff <= 0) {
    return 'Kedaluwarsa';
  }

  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  if (hours > 0) {
    return `${hours}j ${minutes}m`;
  } else {
    return `${minutes}m`;
  }
}
